import { useMemo } from "react";

type DateRange = "all" | "24h" | "7d" | "30d";
type SortOrder = "newest" | "oldest";

interface FilterOptions {
    dateRange: DateRange;
    sortOrder: SortOrder;
}

export function useFilteredItems<T extends { createdAt: string }>(
    items: T[],
    { dateRange, sortOrder }: FilterOptions
) {
    return useMemo(() => {
        const now = Date.now();
        const rangeMsByValue = {
            all: 0,
            "24h": 24 * 60 * 60 * 1000,
            "7d": 7 * 24 * 60 * 60 * 1000,
            "30d": 30 * 24 * 60 * 60 * 1000,
        } as const;

        const rangeMs = rangeMsByValue[dateRange];
        const filteredByDate = items.filter((item) => {
            if (!rangeMs) return true;
            const time = new Date(item.createdAt).getTime();
            if (Number.isNaN(time)) return false;
            return now - time <= rangeMs;
        });

        return [...filteredByDate].sort((a, b) => {
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();
            const safeA = Number.isNaN(timeA) ? 0 : timeA;
            const safeB = Number.isNaN(timeB) ? 0 : timeB;
            return sortOrder === "newest" ? safeB - safeA : safeA - safeB;
        });
    }, [items, dateRange, sortOrder]);
}
